// TODO[sdk-realtime] — this file is intentionally NOT migrated to the SDK
// yet. Realtime channels (`.channel`, `.subscribe`, `postgres_changes`)
// run through the live Supabase client; once SDK-side realtime support
// (workspaceProject.* `subscribe` ops) ships and the wrapper proxies
// realtime-style websockets, the per-table subscriptions below should
// route through getSDK().execute(entity, 'subscribe', filter, handler).
//
// Workspace-aware realtime subscription manager. On workspace switch,
// all active subscriptions must tear down and re-bind with the new
// `workspace_id` filter — Supabase realtime evaluates the filter at
// subscribe time and does not re-evaluate when claims change.
//
// Pattern:
//
//   const mgr = createRealtimeManager()
//   mgr.subscribe('chat_messages', `channel_id=eq.${chId}`, handler)
//   // on workspace switch: mgr.rebindAll()
//   // on unmount:           mgr.unsubscribeAll()
//
// The manager keeps a registry of (table, extraFilter, handler) tuples
// so it can regenerate each subscription with a fresh workspace filter
// when the claim changes.

import { getSupabase } from '../../shared/functions/supabaseClient.js'
import { USE_MULTITENANT, WORKSPACE_SCOPED, activeWorkspaceId } from './scopedClient.js'

export const createRealtimeManager = (root) => {
  const registry = new Map()  // channelName → { table, extraFilter, handler, channel }

  const _buildFilter = (table, extraFilter) => {
    const parts = []
    if (USE_MULTITENANT && WORKSPACE_SCOPED.has(table)) {
      const wsId = activeWorkspaceId(root)
      if (wsId) parts.push(`workspace_id=eq.${wsId}`)
    }
    if (extraFilter) parts.push(extraFilter)
    return parts.length ? parts.join(',') : undefined
  }

  const _bind = (table, extraFilter, handler) => {
    const sb = getSupabase()
    const name = `rt-${table}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const channel = sb.channel(name)
    const filter = _buildFilter(table, extraFilter)
    channel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table, filter },
      handler
    )
    channel.subscribe()
    registry.set(name, { table, extraFilter, handler, channel })
    return name
  }

  const subscribe = (table, extraFilter, handler) => _bind(table, extraFilter, handler)

  const unsubscribe = (name) => {
    const entry = registry.get(name)
    if (!entry) return
    try { entry.channel.unsubscribe() } catch {}
    registry.delete(name)
  }

  const unsubscribeAll = () => {
    for (const [name] of registry) unsubscribe(name)
  }

  // Tear down every active subscription and rebind with the current
  // workspace filter. Called by the workspace switcher after
  // switchWorkspace() succeeds so the new JWT's active_workspace_id
  // is applied to every open channel.
  const rebindAll = () => {
    const snapshot = []
    for (const [name, entry] of registry) {
      snapshot.push({ table: entry.table, extraFilter: entry.extraFilter, handler: entry.handler })
      try { entry.channel.unsubscribe() } catch {}
    }
    registry.clear()
    for (const { table, extraFilter, handler } of snapshot) {
      _bind(table, extraFilter, handler)
    }
  }

  return { subscribe, unsubscribe, unsubscribeAll, rebindAll, size: () => registry.size }
}

// Singleton for the current app runtime — one manager covers every page
// in the intranet. Subscribers register against it; on workspace
// switch, one `rebindAll()` resets every open channel.
let _singleton = null
export const getRealtimeManager = (root) => {
  if (_singleton) return _singleton
  if (!root) throw new Error('getRealtimeManager: root required on first call')
  _singleton = createRealtimeManager(root)
  return _singleton
}
export const resetRealtimeManager = () => { _singleton = null }
